import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Users, TrendingUp, DollarSign, Activity, BarChart3, Zap, Layers, Mic, RefreshCw, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import AdminStatusStrip from '../components/AdminStatusStrip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Analytics = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [byMode, setByMode] = useState(null);
  const [voiceAnalytics, setVoiceAnalytics] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadAnalytics = useCallback(async () => {
    try {
      const [overviewRes, conversionsRes, revenueRes, byModeRes, voiceRes, funnelRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, { withCredentials: true }),
        axios.get(`${API}/analytics/conversions`, { withCredentials: true }),
        axios.get(`${API}/analytics/revenue/details`, { withCredentials: true }),
        axios.get(`${API}/analytics/usage/by-mode`, { withCredentials: true }),
        axios.get(`${API}/analytics/voice`, { withCredentials: true }),
        axios.get(`${API}/admin/activation-funnel`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);

      setOverview(overviewRes.data);
      setConversions(conversionsRes.data);
      setRevenue(revenueRes.data);
      setByMode(byModeRes.data);
      setVoiceAnalytics(voiceRes.data);
      setFunnel(funnelRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Analytics error:', error);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadAnalytics();
  };

  const currency = t('analytics.currency');

  if (loading) {
    return (
      <div className="hussar-app-page">
        <div className="hussar-app-container">
          <div className="hussar-loading">
            <Activity className="w-12 h-12 animate-spin hussar-loader" />
            <p>{t('analytics.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hussar-app-page">
      {/* Header */}
      <header className="hussar-app-header">
        <div className="hussar-app-header-content">
          <Button
            onClick={() => navigate('/app')}
            className="hussar-btn-ghost hussar-btn-icon"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="hussar-app-header-title">
            <BarChart3 className="w-6 h-6" />
            <h1>{t('analytics.headerTitle')}</h1>
          </div>
          <Button
            onClick={() => navigate('/admin/users')}
            className="hussar-btn-ghost hussar-btn-icon"
            title={t('admin.title')}
            data-testid="analytics-users-link"
          >
            <Users className="w-5 h-5" />
          </Button>
          <Button
            onClick={handleRefresh}
            className="hussar-btn-ghost hussar-btn-icon"
            title={t('analytics.refresh')}
            data-testid="analytics-refresh-btn"
            disabled={refreshing}
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="hussar-app-main">
        <div className="hussar-analytics-container">
          <AdminStatusStrip />
          {/* Overview Cards */}
          <div className="hussar-analytics-grid">
            {/* Users Card */}
            <Card className="hussar-analytics-card">
              <div className="hussar-analytics-card-header">
                <Users className="hussar-analytics-icon" />
                <h3>{t('analytics.usersTitle')}</h3>
              </div>
              <div className="hussar-analytics-card-body">
                <div className="hussar-analytics-metric-large">
                  {overview?.users?.total || 0}
                </div>
                <div className="hussar-analytics-breakdown">
                  <div className="hussar-analytics-breakdown-item">
                    <span className="hussar-analytics-label">{t('analytics.labelPremium')}</span>
                    <span className="hussar-analytics-value">{overview?.users?.premium || 0}</span>
                  </div>
                  <div className="hussar-analytics-breakdown-item">
                    <span className="hussar-analytics-label">{t('analytics.labelFree')}</span>
                    <span className="hussar-analytics-value">{overview?.users?.free || 0}</span>
                  </div>
                  <div className="hussar-analytics-breakdown-item">
                    <span className="hussar-analytics-label">{t('analytics.labelNew7d')}</span>
                    <span className="hussar-analytics-value">{overview?.users?.recent_registrations_7d || 0}</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Usage Card */}
            <Card className="hussar-analytics-card">
              <div className="hussar-analytics-card-header">
                <Zap className="hussar-analytics-icon" />
                <h3>{t('analytics.usageTitle')}</h3>
              </div>
              <div className="hussar-analytics-card-body">
                <div className="hussar-analytics-metric-large">
                  {overview?.usage?.prompts_today || 0}
                </div>
                <p className="hussar-analytics-subtitle">{t('analytics.promptsToday')}</p>
                <div className="hussar-analytics-breakdown">
                  <div className="hussar-analytics-breakdown-item">
                    <span className="hussar-analytics-label">{t('analytics.activeToday')}</span>
                    <span className="hussar-analytics-value">{overview?.usage?.active_users_today || 0}</span>
                  </div>
                  <div className="hussar-analytics-breakdown-item">
                    <span className="hussar-analytics-label">{t('analytics.atLimit')}</span>
                    <span className="hussar-analytics-value">{overview?.usage?.users_at_limit_today || 0}</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Revenue Card */}
            <Card className="hussar-analytics-card">
              <div className="hussar-analytics-card-header">
                <DollarSign className="hussar-analytics-icon" />
                <h3>{t('analytics.revenueTitle')}</h3>
              </div>
              <div className="hussar-analytics-card-body">
                <div className="hussar-analytics-metric-large">
                  {revenue?.mrr || 0} {currency}
                </div>
                <p className="hussar-analytics-subtitle">{t('analytics.mrrSubtitle')}</p>
                <div className="hussar-analytics-breakdown">
                  <div className="hussar-analytics-breakdown-item">
                    <span className="hussar-analytics-label">{t('analytics.arrLabel')}</span>
                    <span className="hussar-analytics-value">{revenue?.arr || 0} {currency}</span>
                  </div>
                  <div className="hussar-analytics-breakdown-item">
                    <span className="hussar-analytics-label">{t('analytics.avgLtvLabel')}</span>
                    <span className="hussar-analytics-value">{revenue?.avg_ltv || 0} {currency}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Conversion Funnel */}
          <Card className="hussar-analytics-card hussar-analytics-card-wide">
            <div className="hussar-analytics-card-header">
              <TrendingUp className="hussar-analytics-icon" />
              <h3>{t('analytics.funnelTitle')}</h3>
            </div>
            <div className="hussar-analytics-card-body">
              <div className="hussar-funnel">
                <div className="hussar-funnel-step">
                  <div className="hussar-funnel-number">
                    {conversions?.funnel?.registered_users || 0}
                  </div>
                  <div className="hussar-funnel-label">{t('analytics.stepRegistered')}</div>
                </div>
                
                <div className="hussar-funnel-arrow">
                  <span className="hussar-funnel-rate">
                    {conversions?.conversion_rates?.register_to_prompt || 0}%
                  </span>
                </div>
                
                <div className="hussar-funnel-step">
                  <div className="hussar-funnel-number">
                    {conversions?.funnel?.users_generated_prompt || 0}
                  </div>
                  <div className="hussar-funnel-label">{t('analytics.stepFirstPrompt')}</div>
                </div>
                
                <div className="hussar-funnel-arrow">
                  <span className="hussar-funnel-rate">
                    {conversions?.conversion_rates?.prompt_to_limit || 0}%
                  </span>
                </div>
                
                <div className="hussar-funnel-step">
                  <div className="hussar-funnel-number">
                    {conversions?.funnel?.users_hit_limit || 0}
                  </div>
                  <div className="hussar-funnel-label">{t('analytics.stepHitLimit')}</div>
                </div>
                
                <div className="hussar-funnel-arrow">
                  <span className="hussar-funnel-rate">
                    {conversions?.conversion_rates?.limit_to_premium || 0}%
                  </span>
                </div>
                
                <div className="hussar-funnel-step hussar-funnel-step-final">
                  <div className="hussar-funnel-number">
                    {conversions?.funnel?.premium_users || 0}
                  </div>
                  <div className="hussar-funnel-label">{t('analytics.stepPremium')}</div>
                </div>
              </div>
              
              <div className="hussar-funnel-summary">
                <span className="hussar-funnel-summary-label">{t('analytics.overallConversion')}</span>
                <span className="hussar-funnel-summary-value">
                  {conversions?.conversion_rates?.overall_conversion || 0}%
                </span>
              </div>
            </div>
          </Card>

          {/* Usage by Mode */}
          <Card className="hussar-analytics-card hussar-analytics-card-wide" data-testid="usage-by-mode-card">
            <div className="hussar-analytics-card-header">
              <Layers className="hussar-analytics-icon" />
              <h3>{t('analytics.modesTitle')}</h3>
            </div>
            <div className="hussar-analytics-card-body">
              {byMode && byMode.distribution && byMode.distribution.length > 0 ? (
                <div className="hussar-mode-list">
                  {byMode.distribution.map((m) => (
                    <div key={m.mode} className="hussar-mode-row" data-testid={`mode-row-${m.mode}`}>
                      <div className="hussar-mode-row-head">
                        <span className="hussar-mode-label">{m.label}</span>
                        <span className="hussar-mode-stats">
                          <span className="hussar-mode-count">{m.count}</span>
                          <span className="hussar-mode-pct">{m.percentage}%</span>
                        </span>
                      </div>
                      <div className="hussar-mode-bar">
                        <div
                          className="hussar-mode-bar-fill"
                          style={{ width: `${m.percentage}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="hussar-mode-empty">{t('analytics.modesEmpty')}</p>
              )}
            </div>
          </Card>

          {/* Cohort Experiment card removed 2026-02-10 with LeadWing separation.
              The operator/classic destination experiment was tied to the
              retired /operator route and no longer applies to HussarAi. */}

          {/* Voice Analytics */}
          <Card className="hussar-analytics-card hussar-analytics-card-wide" data-testid="voice-analytics-card">
            <div className="hussar-analytics-card-header">
              <Mic className="hussar-analytics-icon" />
              <h3>{t('analytics.voiceTitle')}</h3>
            </div>
            <div className="hussar-analytics-card-body">
              <div className="hussar-metrics-grid">
                <div className="hussar-metric-item" data-testid="voice-prompts-today">
                  <span className="hussar-metric-label">{t('analytics.voicePromptsToday')}</span>
                  <span className="hussar-metric-value">{voiceAnalytics?.voice_prompts_today || 0}</span>
                </div>
                <div className="hussar-metric-item" data-testid="voice-started-pct">
                  <span className="hussar-metric-label">{t('analytics.voiceStartedPct')}</span>
                  <span className="hussar-metric-value">{voiceAnalytics?.voice_started_pct || 0}%</span>
                </div>
                <div className="hussar-metric-item" data-testid="play-start-rate">
                  <span className="hussar-metric-label">{t('analytics.playStartRate')}</span>
                  <span className="hussar-metric-value">{voiceAnalytics?.play_start_rate || 0}%</span>
                </div>
                <div className="hussar-metric-item" data-testid="play-completion-rate">
                  <span className="hussar-metric-label">{t('analytics.playCompletionRate')}</span>
                  <span className="hussar-metric-value">{voiceAnalytics?.play_completion_rate || 0}%</span>
                </div>
                <div className="hussar-metric-item" data-testid="premium-voice-adoption">
                  <span className="hussar-metric-label">{t('analytics.premiumVoiceAdoption')}</span>
                  <span className="hussar-metric-value">{voiceAnalytics?.premium_voice_adoption_pct || 0}%</span>
                </div>
                <div className="hussar-metric-item" data-testid="avg-transcript-length">
                  <span className="hussar-metric-label">{t('analytics.avgTranscriptLength')}</span>
                  <span className="hussar-metric-value">
                    {voiceAnalytics?.avg_voice_transcript_chars || 0} <span style={{fontSize:'12px',fontWeight:400,opacity:0.7}}>{t('analytics.voiceCharsUnit')}</span>
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Key Metrics Summary */}
          <Card className="hussar-analytics-card hussar-analytics-card-wide">
            <div className="hussar-analytics-card-header">
              <Activity className="hussar-analytics-icon" />
              <h3>{t('analytics.keyMetricsTitle')}</h3>
            </div>
            <div className="hussar-analytics-card-body">
              <div className="hussar-metrics-grid">
                <div className="hussar-metric-item">
                  <span className="hussar-metric-label">{t('analytics.conversionRate')}</span>
                  <span className="hussar-metric-value">{revenue?.conversion_rate || 0}%</span>
                </div>
                <div className="hussar-metric-item">
                  <span className="hussar-metric-label">{t('analytics.pricePerMonth')}</span>
                  <span className="hussar-metric-value">{revenue?.price_per_month || 49} {currency}</span>
                </div>
                <div className="hussar-metric-item">
                  <span className="hussar-metric-label">{t('analytics.premiumUsers')}</span>
                  <span className="hussar-metric-value">{revenue?.premium_users || 0}</span>
                </div>
                <div className="hussar-metric-item">
                  <span className="hussar-metric-label">{t('analytics.mrr')}</span>
                  <span className="hussar-metric-value">{revenue?.mrr || 0} {currency}</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Activation Funnel — admin-only, read-only */}
          <Card className="hussar-analytics-card hussar-analytics-card-wide" data-testid="activation-funnel-card">
            <div className="hussar-analytics-card-header">
              <Filter className="hussar-analytics-icon" />
              <h3 data-testid="activation-funnel-title">Activation Funnel · Aktywacja</h3>
            </div>
            <div className="hussar-analytics-card-body">
              {!funnel ? (
                <p style={{ color: '#94A3B8', fontSize: 14 }} data-testid="activation-funnel-empty">
                  {t('analytics.loading')}
                </p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table
                    data-testid="activation-funnel-table"
                    style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}
                  >
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#94A3B8', borderBottom: '1px solid #334155' }}>
                        <th style={{ padding: '10px 12px' }}>Step</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>24h</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>7d</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>All-time</th>
                        <th style={{ padding: '10px 12px', textAlign: 'right' }}>Conv. from step 1 (all-time)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(funnel.rows || []).map((rowKey, idx) => {
                        const labels = {
                          registered: '1. Registered users',
                          landing_viewed: '2. MOS PreCheck landing viewed',
                          start_clicked: '3. MOS PreCheck start clicked',
                          variant_selected: '4. Procedure selected (temporary_residence_work)',
                          case_created: '5. MOS case created',
                          documents_uploaded: '6. Uploaded ≥ 1 document',
                          report_generated: '7. Generated report',
                          returned_after_first_session: '8. Returned after first session',
                        };
                        const v24 = funnel.metrics?.['24h']?.[rowKey] ?? 0;
                        const v7d = funnel.metrics?.['7d']?.[rowKey] ?? 0;
                        const vAll = funnel.metrics?.all?.[rowKey] ?? 0;
                        const base = funnel.metrics?.all?.registered ?? 0;
                        const conv = base > 0 ? Math.round((vAll / base) * 1000) / 10 : null;
                        return (
                          <tr
                            key={rowKey}
                            data-testid={`activation-funnel-row-${rowKey}`}
                            style={{ borderBottom: idx === (funnel.rows.length - 1) ? 'none' : '1px solid #1E293B' }}
                          >
                            <td style={{ padding: '10px 12px', color: '#E2E8F0' }}>{labels[rowKey] || rowKey}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#F1F5F9' }} data-testid={`activation-funnel-cell-${rowKey}-24h`}>{v24}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#F1F5F9' }} data-testid={`activation-funnel-cell-${rowKey}-7d`}>{v7d}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#F1F5F9' }} data-testid={`activation-funnel-cell-${rowKey}-all`}>{vAll}</td>
                            <td style={{ padding: '10px 12px', textAlign: 'right', color: '#94A3B8' }} data-testid={`activation-funnel-cell-${rowKey}-conv`}>
                              {conv === null ? '—' : `${conv}%`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p style={{ fontSize: 12, color: '#64748B', marginTop: 12 }} data-testid="activation-funnel-footnote">
                    Counts are distinct users per step within each window. Read-only. No PII.
                  </p>
                </div>
              )}
            </div>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default Analytics;
